import { z } from 'zod';

/**
 * Wire types for the `/api/kb/search` endpoint (and the `kb_search` tool
 * exposed to the repo-manager DO + sandbox agents).
 *
 * Kept here — not in `@repo-manager/db` — because these cross the network.
 * The DB package owns the row types; this package owns the public contract.
 */

export const kbEntryType = z.enum(['pattern', 'standard', 'reference_impl', 'lesson']);
export type KbEntryType = z.infer<typeof kbEntryType>;

export const kbSeverity = z.enum(['standard', 'recommended', 'optional']);
export type KbSeverity = z.infer<typeof kbSeverity>;

export const kbStackLayer = z.enum([
  'frontend',
  'api',
  'database',
  'infra',
  'testing',
]);
export type KbStackLayer = z.infer<typeof kbStackLayer>;

export const kbSearchRequest = z.object({
  query: z.string().min(1),
  filters: z
    .object({
      type: kbEntryType.optional(),
      stack_layer: kbStackLayer.optional(),
      severity: kbSeverity.optional(),
    })
    .optional(),
});
export type KbSearchRequest = z.infer<typeof kbSearchRequest>;

export const kbSearchHit = z.object({
  id: z.string().uuid(),
  title: z.string(),
  type: kbEntryType,
  slug: z.string(),
  summary: z.string(),
  severity: kbSeverity,
  similarity: z.number(),
});
export type KbSearchHit = z.infer<typeof kbSearchHit>;

export const kbSearchResponse = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('search'), results: z.array(kbSearchHit) }),
  z.object({
    mode: z.literal('read'),
    entry: z
      .object({
        id: z.string().uuid(),
        slug: z.string(),
        type: kbEntryType,
        severity: kbSeverity,
        title: z.string(),
        summary: z.string(),
        content: z.string(),
        rationale: z.string().nullable(),
        code_example: z.string().nullable(),
      })
      .nullable(),
  }),
]);
export type KbSearchResponse = z.infer<typeof kbSearchResponse>;
